import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
    Check, 
    Crown, 
    Loader2, 
    ExternalLink, 
    AlertCircle,
    Sparkles,
    Zap,
    Shield,
    Clock,
    Plus,
    RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import api from "@/lib/api";

export default function SubscriptionPage() {
    const { user: authUser, updateUser } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userLoading, setUserLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [processingPlan, setProcessingPlan] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [user, setUser] = useState(null);

    const hasActiveSubscription = user?.subscription_status === 'active';

    useEffect(() => {
        const init = async () => {
            if (authUser) {
                setUser(authUser);
            }
            await fetchUserData();
            await loadPlans();
        };
        init();
    }, []);

    useEffect(() => {
        const reference = searchParams.get('reference');
        const verified = searchParams.get('verified');
        const success = searchParams.get('success');
        
        if (reference && !verified) {
            verifyPayment(reference);
        }
        
        if (verified === 'true' || success === 'true') {
            fetchUserData();
            setSearchParams({}, { replace: true });
            toast.success("Purchase successful!");
        }
    }, [searchParams]);

    const loadPlans = async () => {
        try {
            const res = await api.get('/subscriptions/plans');
            setPlans(res.data.plans || res.data || []);
        } catch (error) {
            toast.error("Failed to load subscription plans");
        } finally {
            setLoading(false);
        }
    };

    const fetchUserData = async () => {
        setUserLoading(true);
        try {
            const res = await api.get('/auth/me');
            const userData = res.data.user || res.data;
            
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            
            if (updateUser) {
                updateUser(userData);
            }
            
            return userData;
        } catch (error) {
            if (authUser) {
                setUser(authUser);
            } else {
                const stored = localStorage.getItem('user');
                if (stored) {
                    setUser(JSON.parse(stored));
                }
            }
            return null;
        } finally {
            setUserLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const userData = await fetchUserData();
            if (userData) {
                toast.success("Data refreshed!");
            } else {
                toast.error("Failed to refresh");
            }
        } finally {
            setRefreshing(false);
        }
    };

    const verifyPayment = async (reference) => {
        try {
            const res = await api.get(`/subscriptions/verify/${reference}`);
            if (res.data.status === 'success') {
                toast.success("Payment successful!");
                await fetchUserData();
                setSearchParams({ verified: 'true' }, { replace: true });
                loadPlans();
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.response?.data?.error;
            toast.error(typeof errorMsg === 'string' ? errorMsg : "Payment verification failed");
            setSearchParams({}, { replace: true });
        }
    };

    const handleSubscribe = async (planId, planAmount, creditsOnly = false) => {
        setProcessingPlan(planId);
        try {
            const callbackUrl = `${window.location.origin}/subscription/callback`;
            
            if (planAmount === 0 && !hasActiveSubscription) {
                const res = await api.post('/subscriptions/initialize', { 
                    plan_id: planId, 
                    callback_url: callbackUrl 
                });
                if (res.data.status === 'active') {
                    toast.success("Free plan activated!");
                    await fetchUserData();
                    loadPlans();
                }
                setProcessingPlan(null);
                return;
            }
            
            const endpoint = creditsOnly ? '/subscriptions/buy-credits' : '/subscriptions/initialize';
            
            const res = await api.post(endpoint, { 
                plan_id: planId, 
                callback_url: callbackUrl,
                credits_only: creditsOnly
            });
            
            if (res.data.authorization_url) {
                window.location.href = res.data.authorization_url;
            } else {
                toast.error("No payment URL returned");
                setProcessingPlan(null);
            }
        } catch (error) {
            const errorData = error.response?.data;
            let errorMsg = "Failed to initialize payment";
            if (errorData) {
                if (typeof errorData.error === 'string') errorMsg = errorData.error;
                else if (typeof errorData.message === 'string') errorMsg = errorData.message;
            }
            toast.error(errorMsg);
            setProcessingPlan(null);
        }
    };

    const getSubscriptionDaysLeft = () => {
        if (!user?.subscription_end) return null;
        const end = new Date(user.subscription_end);
        const now = new Date();
        const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        return days > 0 ? days : 0;
    };

    const getCurrentPlanName = () => {
        if (!user?.subscription_plan) return null;
        if (typeof user.subscription_plan === 'object') {
            return user.subscription_plan.name;
        }
        const planId = String(user.subscription_plan);
        const foundPlan = plans.find(p => String(p.id) === planId);
        return foundPlan?.name || null;
    };

    const isCurrentPlan = (plan) => {
        if (!user?.subscription_plan) return false;
        if (user?.subscription_status !== 'active') return false;
        
        if (typeof user.subscription_plan === 'object' && user.subscription_plan !== null) {
            if (user.subscription_plan.id && String(user.subscription_plan.id) === String(plan.id)) return true;
            if (user.subscription_plan.slug && user.subscription_plan.slug === plan.slug) return true;
            if (user.subscription_plan.name && user.subscription_plan.name.toLowerCase() === plan.name?.toLowerCase()) return true;
        }
        
        if (String(user.subscription_plan) === String(plan.id)) return true;
        
        return false;
    };

    const getPlanIcon = (planName) => {
        const name = planName?.toLowerCase() || '';
        if (name.includes('enterprise')) return <Crown className="w-5 h-5" />;
        if (name.includes('business')) return <Shield className="w-5 h-5" />;
        if (name.includes('professional')) return <Zap className="w-5 h-5" />;
        if (name.includes('starter')) return <Sparkles className="w-5 h-5" />;
        return <Clock className="w-5 h-5" />;
    };

    const getPlanColor = (planName) => {
        const name = planName?.toLowerCase() || '';
        if (name.includes('enterprise')) return 'from-purple-500 to-purple-600';
        if (name.includes('business')) return 'from-blue-500 to-blue-600';
        if (name.includes('professional')) return 'from-emerald-500 to-emerald-600';
        if (name.includes('starter')) return 'from-amber-500 to-amber-600';
        return 'from-gray-500 to-gray-600';
    };

    if (userLoading && !user) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                <p className="text-muted-foreground">Loading subscription data...</p>
            </div>
        );
    }

    const daysLeft = getSubscriptionDaysLeft();
    const currentPlanName = getCurrentPlanName();

    return (
        <div className="p-4 md:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
            <div className="text-center space-y-2">
                <h1 className="text-2xl md:text-3xl font-bold">
                    {hasActiveSubscription ? 'Buy Email Credits' : 'Choose Your Plan'}
                </h1>
                <p className="text-muted-foreground">
                    {hasActiveSubscription 
                        ? 'Purchase additional email credits for your account'
                        : 'Select the perfect plan for your business needs'
                    }
                </p>
            </div>

            <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
                <CardContent className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <p className="text-sm text-muted-foreground font-medium">Current Status</p>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 w-6 p-0 hover:bg-primary/10"
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    title="Refresh data"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                <Badge 
                                    variant={hasActiveSubscription ? 'default' : 'secondary'}
                                    className="text-sm px-3 py-1"
                                >
                                    {hasActiveSubscription ? '✓ Active' : 
                                     user?.subscription_status === 'trial' ? '⏱ Trial' : 
                                     '○ Inactive'}
                                </Badge>
                                {currentPlanName && (
                                    <span className="font-semibold text-base md:text-lg">{currentPlanName}</span>
                                )}
                            </div>
                            {user?.subscription_end && hasActiveSubscription && (
                                <p className="text-sm text-muted-foreground">
                                    Renews on{' '}
                                    <span className="font-medium">
                                        {new Date(user.subscription_end).toLocaleDateString('en-US', {
                                            month: 'long', day: 'numeric', year: 'numeric'
                                        })}
                                    </span>
                                </p>
                            )}
                        </div>
                        
                        <div className="flex items-center gap-4">
                            <div className="text-center px-3 py-2 bg-background rounded-lg border min-w-[100px]">
                                <p className="text-xs text-muted-foreground uppercase tracking-wide">Email Credits</p>
                                <p className={`text-xl font-bold ${
                                    (user?.email_credits ?? 0) <= 20 ? 'text-red-500' : 'text-green-500'
                                }`}>
                                    {(user?.email_credits ?? 0).toLocaleString()}
                                </p>
                            </div>
                            
                            {daysLeft !== null && hasActiveSubscription && (
                                <div className="text-center px-3 py-2 bg-background rounded-lg border min-w-[100px]">
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Renews In</p>
                                    <p className={`text-xl font-bold ${
                                        daysLeft <= 7 ? 'text-amber-500' : 'text-primary'
                                    }`}>
                                        {daysLeft} <span className="text-sm font-normal">days</span>
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                    <Loader2 className="w-10 h-10 animate-spin text-primary" />
                    <p className="text-muted-foreground">Loading plans...</p>
                </div>
            ) : plans.length === 0 ? (
                <Card className="py-16">
                    <CardContent className="text-center space-y-4">
                        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground" />
                        <h3 className="text-xl font-semibold">No Plans Available</h3>
                        <Button variant="outline" onClick={loadPlans}>Refresh</Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {plans.map((plan) => {
                        if (!plan?.id) return null;
                        
                        const isCurrent = isCurrentPlan(plan);
                        const amount = parseFloat(plan.amount) || 0;
                        const features = Array.isArray(plan.features) ? plan.features : [];
                        const isPopular = plan.slug === 'professional' || plan.name?.toLowerCase().includes('professional');
                        const credits = plan.email_credits_per_cycle || 0;
                        
                        const isDisabled = isCurrent || processingPlan === plan.id || (hasActiveSubscription && amount === 0);
                        const showBuyCredits = hasActiveSubscription && !isCurrent && amount > 0;
                        
                        return (
                            <Card 
                                key={plan.id}
                                className={`relative flex flex-col transition-all duration-200 ${
                                    isCurrent 
                                        ? 'ring-2 ring-primary shadow-lg bg-primary/5' 
                                        : isPopular 
                                            ? 'ring-2 ring-emerald-500 hover:shadow-xl hover:-translate-y-1' 
                                            : 'hover:shadow-xl hover:-translate-y-1'
                                }`}
                            >
                                {isPopular && !isCurrent && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                                        <Badge className="bg-emerald-500 hover:bg-emerald-500 shadow-lg text-xs">
                                            Most Popular
                                        </Badge>
                                    </div>
                                )}
                                
                                {isCurrent && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                                        <Badge className="bg-primary shadow-lg text-xs">
                                            ✓ Current Plan
                                        </Badge>
                                    </div>
                                )}
                                
                                <CardHeader className="text-center pb-2 pt-6 px-3">
                                    <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-br ${getPlanColor(plan.name)} flex items-center justify-center text-white mb-2 shadow-lg`}>
                                        {getPlanIcon(plan.name)}
                                    </div>
                                    
                                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                                    
                                    <div className="mt-3">
                                        {hasActiveSubscription && !isCurrent ? (
                                            <div className="space-y-1">
                                                <div className="flex items-center justify-center gap-1 text-primary">
                                                    <Sparkles className="w-5 h-5" />
                                                    <span className="text-2xl md:text-3xl font-bold">
                                                        {credits.toLocaleString()}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-muted-foreground">credits</p>
                                                {amount > 0 && (
                                                    <p className="text-sm font-medium text-muted-foreground">
                                                        for {formatCurrency(amount)}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            amount === 0 ? (
                                                <span className="text-2xl md:text-3xl font-bold">Free</span>
                                            ) : (
                                                <div className="flex items-baseline justify-center gap-1">
                                                    <span className="text-xl md:text-2xl font-bold">
                                                        {formatCurrency(amount)}
                                                    </span>
                                                    <span className="text-muted-foreground text-xs">
                                                        /{plan.interval === 'yearly' ? 'yr' : 'mo'}
                                                    </span>
                                                </div>
                                            )
                                        )}
                                    </div>
                                    
                                    {(!hasActiveSubscription || isCurrent) && (
                                        <div className="mt-2 inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                                            <Sparkles className="w-3 h-3" />
                                            {credits.toLocaleString()} credits
                                        </div>
                                    )}
                                </CardHeader>
                                
                                <CardContent className="flex-1 flex flex-col px-3 pb-4">
                                    {(!hasActiveSubscription || isCurrent) && (
                                        <ul className="space-y-1.5 flex-1 mb-4">
                                            {features.slice(0, 5).map((feature, i) => (
                                                <li key={i} className="flex items-start gap-2 text-xs">
                                                    <Check className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                                                    <span className="leading-tight">{feature}</span>
                                                </li>
                                            ))}
                                            {features.length > 5 && (
                                                <li className="text-xs text-muted-foreground pl-5">
                                                    +{features.length - 5} more
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                    
                                    {hasActiveSubscription && !isCurrent && amount > 0 && (
                                        <div className="flex-1 mb-4 space-y-2">
                                            <div className="bg-muted/50 rounded-lg p-3 text-center">
                                                <p className="text-xs text-muted-foreground mb-1">Price per credit</p>
                                                <p className="text-sm font-semibold">
                                                    {formatCurrency(amount / credits)} each
                                                </p>
                                            </div>
                                            <p className="text-xs text-center text-muted-foreground">
                                                Credits added instantly to your account
                                            </p>
                                        </div>
                                    )}
                                    
                                    <Button 
                                        className={`w-full text-sm ${isCurrent ? 'opacity-60' : ''}`}
                                        variant={isCurrent ? "outline" : showBuyCredits ? "default" : isPopular ? "default" : "secondary"}
                                        disabled={isDisabled}
                                        onClick={() => handleSubscribe(plan.id, amount, showBuyCredits)}
                                    >
                                        {processingPlan === plan.id ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Processing...
                                            </>
                                        ) : isCurrent ? (
                                            <>
                                                <Check className="w-4 h-4 mr-1" />
                                                Current Plan
                                            </>
                                        ) : showBuyCredits ? (
                                            <>
                                                <Plus className="w-4 h-4 mr-1" />
                                                Buy {credits.toLocaleString()} Credits
                                            </>
                                        ) : amount === 0 ? (
                                            hasActiveSubscription ? "Already Subscribed" : "Get Started"
                                        ) : (
                                            <>
                                                Subscribe
                                                <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                                            </>
                                        )}
                                    </Button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            <Card className="bg-muted/50">
                <CardContent className="py-4">
                    <div className="text-center space-y-2">
                        <p className="text-sm text-muted-foreground">
                            Secure payments powered by{' '}
                            <span className="font-semibold text-[#00C3F7]">Paystack</span>
                        </p>
                        <div className="flex flex-wrap justify-center gap-2">
                            <Badge variant="outline" className="text-xs">Mobile Money</Badge>
                            <Badge variant="outline" className="text-xs">Visa</Badge>
                            <Badge variant="outline" className="text-xs">Mastercard</Badge>
                            <Badge variant="outline" className="text-xs">Bank Transfer</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            All prices in Ghana Cedis (GHS). Cancel anytime.
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}